import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchSuiteServiceRequests } from 'src/store/actions/serviceRequests';
import { selectServiceRequests } from 'src/store/selectors/serviceRequests';
import ServiceRequestsTable from '../../components/ServiceRequestsTable/ServiceRequestsTable_legacy';
import { clearSearch } from 'src/store/actions/search';
import Loader from '../../components/Loader';
import { selectLoader } from 'src/store/selectors/loader';
import { PATHS } from 'src/common/constants';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { fetchSuiteServiceRequestCSVExportDownloadList } from 'src/store/actions/csvExport';
import { DetailedSuite, Suite } from 'src/components/ContactForm/types';
import { User } from 'src/components/Messages/types';

interface ServiceRequest {
  [key: string]: any;
}

interface RootState {
  paginationCount: {
    count: number;
  };
  serviceRequestStatusPageCount: {
    pageCount: number;
  };
  [key: string]: any;
}

interface SuiteServiceRequestsProps {
  suite: DetailedSuite;
}

const determineToDisableCreateRequestButtonBasedOnWarrantyStartDate = (
  assetWarrantyDefaultStartDate: string
): boolean => {
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  const warrantyStartDate = new Date(assetWarrantyDefaultStartDate);
  warrantyStartDate.setHours(0, 0, 0, 0);

  return warrantyStartDate > currentDate;
};

const SuiteServiceRequests = ({ suite }: SuiteServiceRequestsProps) => {
  const dispatch = useDispatch();

  const user = useSelector(selectAuthenticatedUser) as User;
  const paginationCount = useSelector(
    (state: RootState) => state.paginationCount.count
  );
  const serviceRequests = useSelector(
    selectServiceRequests
  ) as ServiceRequest[];
  const loader = useSelector(selectLoader) as boolean;

  const [searchString, setSearchString] = useState<string>('');

  const isCreateRequestDisabled: boolean = useMemo(() => {
    return determineToDisableCreateRequestButtonBasedOnWarrantyStartDate(
      suite.assetWarrantyDefaultStartDate
    );
  }, [suite.assetWarrantyDefaultStartDate]);

  const onFetchSuiteServiceRequests = useCallback(
    (
      suiteId: string | number,
      roleName: string,
      page: number,
      search: string,
      reset?: boolean
    ) =>
      dispatch(
        fetchSuiteServiceRequests(suiteId, roleName, page, search, reset)
      ),
    [dispatch]
  );

  const handleClearSearch = useCallback(
    () => dispatch(clearSearch()),
    [dispatch]
  );

  const handleFetchSuiteServiceRequestCSVExportDownloadList = useCallback(
    (suiteId: string | number, roleName: string, search: string) =>
      dispatch(
        fetchSuiteServiceRequestCSVExportDownloadList(suiteId, roleName, search)
      ),
    [dispatch]
  );

  useEffect(() => {
    onFetchSuiteServiceRequests(suite.id, user.roles[0].role.name, 1, '');
    handleClearSearch();
  }, [suite.id, user.roles, onFetchSuiteServiceRequests, handleClearSearch]);

  const loadMoreServiceRequest = useCallback(async () => {
    onFetchSuiteServiceRequests(
      suite.id,
      user.roles[0].role.name,
      paginationCount + 1,
      searchString
    );
  }, [
    onFetchSuiteServiceRequests,
    suite.id,
    user.roles,
    paginationCount,
    searchString,
  ]);

  const search = useCallback(
    async (text: string) => {
      setSearchString(text);
      onFetchSuiteServiceRequests(
        suite.id,
        user.roles[0].role.name,
        1,
        text,
        true
      );
    },
    [onFetchSuiteServiceRequests, suite.id, user.roles]
  );

  const downloadCSV = useCallback(() => {
    handleFetchSuiteServiceRequestCSVExportDownloadList(
      suite.id,
      user.roles[0].role.name,
      searchString
    );
  }, [
    handleFetchSuiteServiceRequestCSVExportDownloadList,
    suite.id,
    user.roles,
    searchString,
  ]);

  const serviceRequestConfig = useMemo(
    () => ({
      property_id: suite.propertyId,
      propertyId: suite.propertyId,
      suiteId: suite.id,
      suite_id: suite.id,
      suiteFloorPLan:
        suite.floorPlanURL && suite.floorPlanURL.length > 0
          ? suite.floorPlanURL
          : '',
      asset_warranty_star_date: suite.assetWarrantyDefaultStartDate,
    }),
    [
      suite.propertyId,
      suite.id,
      suite.floorPlanURL,
      suite.assetWarrantyDefaultStartDate,
    ]
  );

  const navigateTo = useMemo(
    () => PATHS.SUITES_VIEW.replace(':id', suite.propertyId + '&' + suite.id),
    [suite.propertyId, suite.id]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      search(e.target.value.trim());
    },
    [search]
  );

  return loader ? (
    <Loader />
  ) : (
    <ServiceRequestsTable
      download={downloadCSV}
      propertyDisabled={true}
      serviceRequests={serviceRequests}
      onSearch={handleSearchChange}
      serviceRequest={serviceRequestConfig}
      loadServiceRequest={loadMoreServiceRequest}
      from="Suite"
      navigateTo={navigateTo}
      introContent={true}
      isCreateRequestDisabled={isCreateRequestDisabled}
    />
  );
};

export default SuiteServiceRequests;
